import BaseButton from 'components/atoms/baseButton';
import PersonWorkspaceIcon from 'assets/img/person-workspace.svg?react';
import BaseTableNew from 'components/atoms/base-table/new';
import { useEffect, type FC } from 'react';
import {
  usePartialsTableDataQueryFilter,
  useConsoleTable,
  useGetPartialsTableData,
  CCM_CONSOLE_PAGE_SIZE,
} from 'infrastructure/hooks/ccm/console';
import { useTranslation } from 'react-i18next';
import { checkIsPageAvailable } from 'infrastructure/hooks/ccm/console/utils';

import { AccountsSelect } from '../select-boxes/select-account';
import CcmConsoleTableBlock from '../table-block';
import s from './styles.module.scss';
import { useColumns } from './columns';
import { CcmInteractionStatusSelect } from '../select-boxes/ccm-interaction-status';

import type {
  IPartialsTableData,
  IPartialsTableDataFilters,
} from 'infrastructure/interfaces';

interface PartialsTableProps {}

const PartialsTable: FC<PartialsTableProps> = () => {
  const { filter, updateFilter } = usePartialsTableDataQueryFilter();
  const { data, isLoading } = useGetPartialsTableData(filter);
  const columns = useColumns({ filter });
  const { t } = useTranslation();
  const items = data?.data || [];
  const total = data?.count || 0;

  const { onSort, pushToQueue, rowSelection, disableAddToQueue } =
    useConsoleTable<IPartialsTableDataFilters, IPartialsTableData>({
      filter,
      updateFilter,
      tablePrefix: 'p-',
      excludeSortFields: [
        'p-page',
        'p-clinicGuid',
        'p-parentClinicGuid',
        'patientGuid',
      ],
      tableName: 'partials',
    });

  useEffect(() => {
    const isPageExist = checkIsPageAvailable(
      filter['p-page'],
      total,
      items.length,
    );
    if (!isPageExist && !isLoading && filter['p-page'] !== 1) {
      updateFilter({ 'p-page': undefined });
    }
  }, [filter['p-page'], total, isLoading, items.length]);

  return (
    <CcmConsoleTableBlock title={t('labels.partials')} total={total}>
      <div className={s['select-wrapper']}>
        <AccountsSelect
          tablePrefix="p-"
          updateFilter={updateFilter}
          parentClinicGuid={filter['p-parentClinicGuid']}
          clinicGuid={filter['p-clinicGuid']}
        />
        <CcmInteractionStatusSelect
          value={filter.ccmInteractionStatus}
          updateFilter={updateFilter}
        />
        <BaseButton
          label={t('labels.addToWorkQueue')}
          className={s['add-queue-btn']}
          icon={<PersonWorkspaceIcon />}
          onClick={pushToQueue}
          disabled={disableAddToQueue}
        />
      </div>
      <BaseTableNew
        rowKey="guid"
        items={items}
        columns={columns}
        bordered
        loading={isLoading}
        pagination={{
          current: filter['p-page'],
          pageSize: CCM_CONSOLE_PAGE_SIZE,
          total,
          position: ['bottomCenter'],
        }}
        onChangePage={(newPage) => {
          updateFilter({ 'p-page': newPage });
        }}
        dataCy="partials-patients-table"
        tableRowSelection={rowSelection}
        onSort={onSort}
        className={s.table}
        rowHoverable={false}
      />
    </CcmConsoleTableBlock>
  );
};

export default PartialsTable;
